/**
 * ===========================================================================
 *  UI CANVAS PRIMITIVES
 * ===========================================================================
 *
 * Everything the HUD needs that CSS cannot draw well: metal-gradient numerals,
 * the procedural portrait plate, bevelled frames and the faction crest.
 *
 * WHY NUMERALS ARE DRAWN HERE AND NOT SET AS TEXT
 * A large DOM numeral is a raw system glyph, flat-filled — failure mode #10 on
 * the reference plate ("system-font HUD"). Drawn on a canvas we can give a
 * numeral the treatment a shipped game gives it: a condensed + sheared frame,
 * a cut dark outline, a four-stop metal ramp down the glyph, a hot top
 * highlight and a soft drop shadow. It is still a Liberation Sans skeleton, but
 * it no longer reads as browser text.
 *
 * Everything here is called on value CHANGE or on resize, never per frame.
 */

/** Metal ramps. Stops run top -> bottom of the glyph box. */
export const RAMP = {
  gold:   [[0, '#fffaea'], [0.30, '#f7dfa4'], [0.52, '#e2b95f'], [0.62, '#a9762a'], [1, '#f3d891']],
  ember:  [[0, '#fff4d8'], [0.28, '#ffd07a'], [0.54, '#ff8a2a'], [0.66, '#c53c08'], [1, '#ffb960']],
  bone:   [[0, '#ffffff'], [0.34, '#efe6d2'], [0.58, '#b9ad94'], [0.68, '#7d735e'], [1, '#e6dbc4']],
  blood:  [[0, '#ffe6dc'], [0.30, '#ff9f86'], [0.54, '#ef4f31'], [0.66, '#8e1a0a'], [1, '#ff7f5c']],
  steel:  [[0, '#f6fbff'], [0.32, '#cfdcea'], [0.55, '#8e9fb2'], [0.66, '#4f5c6b'], [1, '#c3d2e0']],
};

const SANS = '"Liberation Sans","Helvetica Neue",Helvetica,Arial,"DejaVu Sans",sans-serif';
import { GAME } from '../core/theme.js';

const SERIF = '"Liberation Serif","Times New Roman","DejaVu Serif",Georgia,serif';

/** Size a canvas to `w x h` CSS px at `dpr`, returning its 2d context. */
export function fit(cv, w, h, dpr) {
  const bw = Math.max(1, Math.round(w * dpr)), bh = Math.max(1, Math.round(h * dpr));
  if (cv.width !== bw || cv.height !== bh) { cv.width = bw; cv.height = bh; }
  const ctx = cv.getContext('2d');
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, w, h);
  return ctx;
}

function ramp(ctx, name, y0, y1) {
  const g = ctx.createLinearGradient(0, y0, 0, y1);
  const stops = RAMP[name] || RAMP.gold;
  for (let i = 0; i < stops.length; i++) g.addColorStop(stops[i][0], stops[i][1]);
  return g;
}

/**
 * Draw `text` as a display numeral.
 *   o = { size, align:'right'|'center'|'left', ramp, condense, shear, glow,
 *         outline, x, y }
 * Coordinates are CSS px inside the already-fitted context.
 */
export function numeral(ctx, text, w, h, o = {}) {
  const size = o.size || h * 0.86;
  const cond = o.condense === undefined ? 0.88 : o.condense;
  const shear = o.shear === undefined ? -0.13 : o.shear;
  const align = o.align || 'right';
  const bx = o.x !== undefined ? o.x : (align === 'right' ? w - size * 0.10 : align === 'center' ? w * 0.5 : size * 0.10);
  const by = o.y !== undefined ? o.y : h * 0.5;

  ctx.save();
  ctx.translate(bx, by);
  ctx.transform(cond, 0, shear, 1, 0, 0);
  ctx.font = `700 ${size}px ${SANS}`;
  ctx.textAlign = align;
  ctx.textBaseline = 'middle';
  ctx.lineJoin = 'round';

  // 1. soft ground shadow so the glyph sits on top of the battlefield
  ctx.save();
  ctx.translate(0, size * 0.05);
  ctx.shadowColor = 'rgba(0,0,0,.85)';
  ctx.shadowBlur = size * 0.30;
  ctx.fillStyle = 'rgba(0,0,0,.9)';
  ctx.fillText(text, 0, 0);
  ctx.restore();

  // 2. outer cut — two passes, wide dark then a thin warm keyline
  if (o.outline !== false) {
    ctx.strokeStyle = 'rgba(12,8,4,.96)';
    ctx.lineWidth = size * 0.135;
    ctx.strokeText(text, 0, 0);
    ctx.strokeStyle = o.key || 'rgba(120,86,34,.85)';
    ctx.lineWidth = size * 0.055;
    ctx.strokeText(text, 0, 0);
  }

  // 3. the metal
  if (o.glow) {
    ctx.shadowColor = o.glow;
    ctx.shadowBlur = size * (o.glowR || 0.34);
  }
  ctx.fillStyle = ramp(ctx, o.ramp || 'gold', -size * 0.52, size * 0.52);
  ctx.fillText(text, 0, 0);
  ctx.shadowBlur = 0;

  // 4. hot top edge: the same glyph clipped to its upper third
  ctx.save();
  ctx.beginPath();
  ctx.rect(-w * 2, -size * 0.55, w * 4, size * 0.34);
  ctx.clip();
  ctx.fillStyle = 'rgba(255,255,255,.42)';
  ctx.fillText(text, 0, -size * 0.012);
  ctx.restore();

  ctx.restore();
}

/** Small tracked label in the sans stack. */
export function label(ctx, text, x, y, size, color, track, align = 'left') {
  ctx.save();
  ctx.font = `700 ${size}px ${SANS}`;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = color;
  const t = track || 0;
  let total = 0;
  for (let i = 0; i < text.length; i++) total += ctx.measureText(text[i]).width + t;
  total -= t;
  let cx = align === 'right' ? x - total : align === 'center' ? x - total * 0.5 : x;
  for (let i = 0; i < text.length; i++) {
    ctx.fillText(text[i], cx, y);
    cx += ctx.measureText(text[i]).width + t;
  }
  ctx.restore();
  return total;
}

/** Octagon path (cut corners) — the frame silhouette used all over the HUD. */
export function octagon(ctx, x, y, w, h, c) {
  ctx.beginPath();
  ctx.moveTo(x + c, y);
  ctx.lineTo(x + w - c, y);
  ctx.lineTo(x + w, y + c);
  ctx.lineTo(x + w, y + h - c);
  ctx.lineTo(x + w - c, y + h);
  ctx.lineTo(x + c, y + h);
  ctx.lineTo(x, y + h - c);
  ctx.lineTo(x, y + c);
  ctx.closePath();
}

/**
 * The portrait plate: a helmeted bust in near-silhouette, rim-lit from the
 * left, over a warm faction ground, inside a cut-corner gold frame.
 *
 * A face is deliberately not modelled in detail — at 78 px a rendered face is
 * a smudge. What reads at that size is the SILHOUETTE (helmet mass, plume,
 * pauldrons wider than the neck) plus one rim light, which is exactly what the
 * reference plate says makes a hero readable.
 */
export function portrait(ctx, w, h, o = {}) {
  const c = Math.min(w, h) * 0.13;
  const hot = o.hot || 0;                       // 0..1 flash when hurt

  ctx.save();
  octagon(ctx, 1, 1, w - 2, h - 2, c);
  ctx.clip();

  // ground: warm radial, faction-tinted
  const g = ctx.createRadialGradient(w * 0.36, h * 0.30, w * 0.05, w * 0.5, h * 0.55, w * 0.86);
  g.addColorStop(0, o.bg0 || '#6a3722');
  g.addColorStop(0.55, o.bg1 || '#331913');
  g.addColorStop(1, '#0b0705');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, w, h);

  // faint sunburst rays behind the head
  ctx.save();
  ctx.globalAlpha = 0.16;
  ctx.translate(w * 0.5, h * 0.46);
  for (let i = 0; i < 12; i++) {
    ctx.rotate(Math.PI / 6);
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.lineTo(w * 0.9, -w * 0.05);
    ctx.lineTo(w * 0.9, w * 0.05);
    ctx.closePath();
    ctx.fillStyle = i % 2 ? 'rgba(255,190,110,.55)' : 'rgba(255,150,60,.18)';
    ctx.fill();
  }
  ctx.restore();

  const cx = w * 0.5, S = w;

  // --- pauldrons / chest, wider than the head (the DW wedge)
  ctx.beginPath();
  ctx.moveTo(cx - S * 0.56, h + 2);
  ctx.quadraticCurveTo(cx - S * 0.50, h * 0.80, cx - S * 0.27, h * 0.735);
  ctx.quadraticCurveTo(cx, h * 0.70, cx + S * 0.27, h * 0.735);
  ctx.quadraticCurveTo(cx + S * 0.50, h * 0.80, cx + S * 0.56, h + 2);
  ctx.closePath();
  const gb = ctx.createLinearGradient(cx - S * 0.5, h * 0.7, cx + S * 0.4, h);
  gb.addColorStop(0, '#6a5140'); gb.addColorStop(0.30, '#241b16'); gb.addColorStop(1, '#0c0807');
  ctx.fillStyle = gb; ctx.fill();

  // shoulder trim
  ctx.strokeStyle = o.trim || 'rgba(226,180,96,.5)';
  ctx.lineWidth = Math.max(1, S * 0.016);
  ctx.stroke();

  // --- neck
  ctx.beginPath();
  ctx.moveTo(cx - S * 0.10, h * 0.60); ctx.lineTo(cx + S * 0.10, h * 0.60);
  ctx.lineTo(cx + S * 0.13, h * 0.78); ctx.lineTo(cx - S * 0.13, h * 0.78);
  ctx.closePath();
  ctx.fillStyle = '#171110'; ctx.fill();

  // --- hair mass behind the helmet
  ctx.beginPath();
  ctx.ellipse(cx, h * 0.45, S * 0.235, h * 0.235, 0, 0, Math.PI * 2);
  ctx.fillStyle = '#100c0b'; ctx.fill();

  // --- face plane (dark, but not black — it catches the key)
  ctx.beginPath();
  ctx.moveTo(cx - S * 0.145, h * 0.395);
  ctx.quadraticCurveTo(cx - S * 0.165, h * 0.545, cx, h * 0.625);
  ctx.quadraticCurveTo(cx + S * 0.165, h * 0.545, cx + S * 0.145, h * 0.395);
  ctx.closePath();
  const gf = ctx.createLinearGradient(cx - S * 0.16, 0, cx + S * 0.14, 0);
  gf.addColorStop(0, '#c08a5e'); gf.addColorStop(0.34, '#8a5c3a');
  gf.addColorStop(0.72, '#4a2e1e'); gf.addColorStop(1, '#22150f');
  ctx.fillStyle = gf; ctx.fill();

  // brow shadow + eye slit
  ctx.fillStyle = 'rgba(0,0,0,.62)';
  ctx.fillRect(cx - S * 0.145, h * 0.435, S * 0.29, h * 0.052);
  ctx.fillStyle = 'rgba(255,226,170,.85)';
  ctx.fillRect(cx - S * 0.105, h * 0.452, S * 0.058, h * 0.017);
  ctx.fillRect(cx + S * 0.048, h * 0.452, S * 0.058, h * 0.017);
  // cheek + jaw catch, so the face is not a flat plane
  ctx.fillStyle = 'rgba(255,206,152,.20)';
  ctx.beginPath();
  ctx.moveTo(cx - S * 0.135, h * 0.500);
  ctx.quadraticCurveTo(cx - S * 0.100, h * 0.575, cx - S * 0.020, h * 0.612);
  ctx.quadraticCurveTo(cx - S * 0.090, h * 0.545, cx - S * 0.095, h * 0.492);
  ctx.closePath(); ctx.fill();

  // --- helmet: dome + brim + side flaps
  ctx.beginPath();
  ctx.moveTo(cx - S * 0.235, h * 0.435);
  ctx.quadraticCurveTo(cx - S * 0.225, h * 0.185, cx, h * 0.175);
  ctx.quadraticCurveTo(cx + S * 0.225, h * 0.185, cx + S * 0.235, h * 0.435);
  ctx.lineTo(cx + S * 0.265, h * 0.405);
  ctx.quadraticCurveTo(cx, h * 0.325, cx - S * 0.265, h * 0.405);
  ctx.closePath();
  const gh = ctx.createLinearGradient(cx - S * 0.26, h * 0.18, cx + S * 0.22, h * 0.44);
  // Four stops, overridable per warrior so the roster's three plates are three
  // different men rather than one plate with three plume colours.
  const HM = o.helm || ['#d8b46a', '#7a5825', '#2c2010', '#6a5024'];
  gh.addColorStop(0, HM[0]); gh.addColorStop(0.24, HM[1]);
  gh.addColorStop(0.58, HM[2]); gh.addColorStop(1, HM[3]);
  ctx.fillStyle = gh; ctx.fill();

  // helmet crest ridge
  ctx.beginPath();
  ctx.moveTo(cx, h * 0.175);
  ctx.quadraticCurveTo(cx - S * 0.03, h * 0.30, cx, h * 0.40);
  ctx.quadraticCurveTo(cx + S * 0.03, h * 0.30, cx, h * 0.175);
  ctx.closePath();
  ctx.fillStyle = o.crest || 'rgba(246,214,140,.75)'; ctx.fill();

  // plume
  ctx.beginPath();
  ctx.moveTo(cx - S * 0.03, h * 0.185);
  ctx.quadraticCurveTo(cx - S * 0.10, h * 0.02, cx + S * 0.16, h * -0.02);
  ctx.quadraticCurveTo(cx + S * 0.06, h * 0.12, cx + S * 0.05, h * 0.19);
  ctx.closePath();
  ctx.fillStyle = o.plume || 'rgba(198,52,34,.92)'; ctx.fill();

  // --- rim light down the left edge of the whole bust
  ctx.save();
  ctx.globalCompositeOperation = 'lighter';
  const gr = ctx.createLinearGradient(cx - S * 0.6, 0, cx - S * 0.05, 0);
  gr.addColorStop(0, 'rgba(255,196,110,0)');
  gr.addColorStop(0.58, 'rgba(255,206,132,.34)');
  gr.addColorStop(1, 'rgba(255,226,170,0)');
  ctx.fillStyle = gr;
  ctx.fillRect(0, h * 0.14, w * 0.52, h);
  ctx.restore();

  // hurt flash
  if (hot > 0.001) {
    ctx.fillStyle = `rgba(210,32,20,${0.5 * hot})`;
    ctx.fillRect(0, 0, w, h);
  }

  // inner shading so the plate is not evenly lit
  const gv = ctx.createRadialGradient(w * 0.42, h * 0.34, w * 0.1, w * 0.5, h * 0.5, w * 0.78);
  gv.addColorStop(0, 'rgba(0,0,0,0)'); gv.addColorStop(1, 'rgba(0,0,0,.42)');
  ctx.fillStyle = gv; ctx.fillRect(0, 0, w, h);
  ctx.restore();

  // --- frame
  octagon(ctx, 1.5, 1.5, w - 3, h - 3, c);
  ctx.strokeStyle = 'rgba(6,4,3,.95)';
  ctx.lineWidth = Math.max(2, w * 0.052);
  ctx.stroke();
  const gfm = ctx.createLinearGradient(0, 0, w, h);
  gfm.addColorStop(0, '#f6dfa8'); gfm.addColorStop(0.45, '#a5802f');
  gfm.addColorStop(0.7, '#5e441a'); gfm.addColorStop(1, '#e2c079');
  ctx.strokeStyle = gfm;
  ctx.lineWidth = Math.max(1, w * 0.022);
  ctx.stroke();

  // corner studs
  ctx.fillStyle = 'rgba(250,228,176,.9)';
  const r = Math.max(1, w * 0.017);
  for (const [px, py] of [[c * 0.9, c * 0.9], [w - c * 0.9, c * 0.9], [c * 0.9, h - c * 0.9], [w - c * 0.9, h - c * 0.9]]) {
    ctx.beginPath(); ctx.arc(px, py, r, 0, Math.PI * 2); ctx.fill();
  }
}

/** Faction crest used on the title screen: a bannered glaive in a ring. */
export function crest(ctx, w, h) {
  const cx = w * 0.5, cy = h * 0.5, R = Math.min(w, h) * 0.46;
  ctx.save();
  ctx.translate(cx, cy);

  const g = ctx.createRadialGradient(0, -R * 0.2, R * 0.1, 0, 0, R);
  g.addColorStop(0, 'rgba(90,52,22,.55)'); g.addColorStop(1, 'rgba(10,7,5,.1)');
  ctx.beginPath(); ctx.arc(0, 0, R, 0, Math.PI * 2); ctx.fillStyle = g; ctx.fill();

  ctx.lineWidth = Math.max(1, R * 0.035);
  ctx.strokeStyle = 'rgba(232,196,124,.85)';
  ctx.beginPath(); ctx.arc(0, 0, R, 0, Math.PI * 2); ctx.stroke();
  ctx.lineWidth = Math.max(1, R * 0.012);
  ctx.strokeStyle = 'rgba(232,196,124,.4)';
  ctx.beginPath(); ctx.arc(0, 0, R * 0.87, 0, Math.PI * 2); ctx.stroke();

  // ticks
  ctx.strokeStyle = 'rgba(232,196,124,.55)';
  for (let i = 0; i < 24; i++) {
    const a = (i / 24) * Math.PI * 2;
    const r0 = i % 6 === 0 ? R * 0.74 : R * 0.80;
    ctx.beginPath();
    ctx.moveTo(Math.cos(a) * r0, Math.sin(a) * r0);
    ctx.lineTo(Math.cos(a) * R * 0.86, Math.sin(a) * R * 0.86);
    ctx.stroke();
  }

  // crossed glaives
  for (const s of [-1, 1]) {
    ctx.save();
    ctx.rotate(s * 0.42);
    ctx.fillStyle = 'rgba(214,182,120,.9)';
    ctx.fillRect(-R * 0.035, -R * 0.66, R * 0.07, R * 1.32);
    ctx.beginPath();
    ctx.moveTo(0, -R * 0.80);
    ctx.lineTo(R * 0.20, -R * 0.52);
    ctx.lineTo(0, -R * 0.58);
    ctx.lineTo(-R * 0.12, -R * 0.50);
    ctx.closePath();
    ctx.fillStyle = 'rgba(246,226,178,.95)';
    ctx.fill();
    ctx.restore();
  }

  // banner
  ctx.beginPath();
  ctx.moveTo(-R * 0.30, -R * 0.28);
  ctx.lineTo(R * 0.30, -R * 0.28);
  ctx.lineTo(R * 0.30, R * 0.30);
  ctx.lineTo(0, R * 0.16);
  ctx.lineTo(-R * 0.30, R * 0.30);
  ctx.closePath();
  const gb = ctx.createLinearGradient(-R * 0.3, -R * 0.3, R * 0.3, R * 0.3);
  gb.addColorStop(0, '#9d2418'); gb.addColorStop(1, '#4d0f08');
  ctx.fillStyle = gb; ctx.fill();
  ctx.strokeStyle = 'rgba(240,214,158,.8)';
  ctx.lineWidth = Math.max(1, R * 0.02); ctx.stroke();

  ctx.font = `700 ${R * 0.42}px ${SERIF}`;
  ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
  ctx.fillStyle = 'rgba(250,232,188,.95)';
  // The initial is the game's, not a hand-typed letter: this shield read 'S'
  // for months after the title stopped saying Sundered Banners.
  ctx.fillText(GAME.name.charAt(0), 0, -R * 0.02);
  ctx.restore();
}
